import { createTheme, ThemeProvider } from "@mui/material/styles";
import { RefineThemes } from "@refinedev/mui";
import "@mui/x-data-grid/themeAugmentation";
import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ColorModeContext } from "./context";

// DataGrid takes its font size from theme.typography.body2 (14px by default);
// the tables are dense enough that 10px reads better across every list page.
const TABLE_FONT_SIZE = "10px";

// Status / type / tag pills. Chip's own "small" size is 13px on a 24px pill,
// which towers over 10px cell text, so the pill is resized to match. Scoped to
// the grid so chips elsewhere (forms, show pages) keep their normal size.
const PILL_FONT_SIZE = "9px";

const withTableFontSize = (base: Parameters<typeof createTheme>[0]) =>
  createTheme(base, {
    components: {
      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontSize: TABLE_FONT_SIZE,
            "& .MuiChip-root": {
              fontSize: PILL_FONT_SIZE,
              height: 18,
            },
            "& .MuiChip-label": {
              paddingLeft: 6,
              paddingRight: 6,
            },
          },
        },
      },
    },
  });

export const ColorModeContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const colorModeFromLocalStorage = localStorage.getItem("colorMode");
  const isSystemPreferenceDark = window?.matchMedia(
    "(prefers-color-scheme: dark)"
  ).matches;

  const systemPreference = isSystemPreferenceDark ? "dark" : "light";
  const [mode, setMode] = useState(
    colorModeFromLocalStorage || systemPreference
  );

  useEffect(() => {
    window.localStorage.setItem("colorMode", mode);
  }, [mode]);

  const setColorMode = () => {
    if (mode === "light") {
      setMode("dark");
    } else {
      setMode("light");
    }
  };

  const theme = useMemo(
    () =>
      withTableFontSize(
        mode === "light" ? RefineThemes.Blue : RefineThemes.BlueDark
      ),
    [mode]
  );

  return (
    <ColorModeContext.Provider
      value={{
        setMode: setColorMode,
        mode,
      }}
    >
      <ThemeProvider
        // you can change the theme colors here. example: mode === "light" ? RefineThemes.Magenta : RefineThemes.MagentaDark
        theme={theme}
      >
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
